import { DestroyRef, effect, inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ClientSessionService } from 'src/core/services/client-session/client-session.service';
import { TenantContextService } from 'src/core/services/tenant-context/tenant-context.service';
import { ModalService } from 'src/utils/modal.service';

const STARTED_AT_KEY = 'pc_session_started_at';
const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;
const REARM_THROTTLE_MS = 1000;

const EXPIRY_TITLE: Record<SessionExpiryReason, string> = {
  idle: 'Sesión expirada por inactividad',
  max: 'Tu sesión alcanzó el tiempo máximo permitido',
};

export type SessionExpiryReason = 'idle' | 'max';

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private readonly clientSession = inject(ClientSessionService);
  private readonly modalService = inject(ModalService);
  private readonly tenant = inject(TenantContextService);
  private readonly destroyRef = inject(DestroyRef);

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private maxTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRearmAt = 0;
  private listening = false;

  private readonly onActivity = () => {
    const now = Date.now();
    if (now - this.lastRearmAt < REARM_THROTTLE_MS) return;
    this.lastRearmAt = now;
    this.armIdle();
  };

  constructor() {
    effect(() => {
      if (this.clientSession.session()) this.start();
      else this.stop();
    });

    this.destroyRef.onDestroy(() => this.stop());
  }

  private start(): void {
    if (!sessionStorage.getItem(STARTED_AT_KEY)) {
      sessionStorage.setItem(STARTED_AT_KEY, String(Date.now()));
    }

    if (!this.listening) {
      ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, this.onActivity, { passive: true }));
      this.listening = true;
    }

    this.armIdle();
    this.armMax();
  }

  private stop(): void {
    this.clearTimers();
    sessionStorage.removeItem(STARTED_AT_KEY);

    if (this.listening) {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, this.onActivity));
      this.listening = false;
    }
  }

  private idleTimeoutMs(): number {
    const seconds = this.clientSession.current?.idleTimeoutSeconds;
    return seconds && seconds > 0 ? seconds * 1000 : environment.idleTimeoutMs;
  }

  private armIdle(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.expire('idle'), this.idleTimeoutMs());
  }

  // El máximo es absoluto desde el inicio de sesión: la actividad del cliente no lo extiende.
  private armMax(): void {
    if (this.maxTimer) clearTimeout(this.maxTimer);

    const expiresAt = this.clientSession.current?.sessionExpiresAt;
    const deadline = expiresAt
      ? new Date(expiresAt).getTime()
      : Number(sessionStorage.getItem(STARTED_AT_KEY) ?? Date.now()) + environment.maxSessionMs;

    const remaining = deadline - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) {
      this.expire('max');
      return;
    }
    this.maxTimer = setTimeout(() => this.expire('max'), remaining);
  }

  private expire(reason: SessionExpiryReason): void {
    this.clearTimers();
    this.clientSession.clearSession();

    this.modalService.show({
      type: 'warning',
      title: EXPIRY_TITLE[reason],
      message: 'Por tu seguridad cerramos la sesión. Inicia sesión nuevamente para continuar.',
      confirmText: 'Iniciar sesión',
      closeOnBackdrop: false,
      onConfirm: () => this.tenant.goToLogin(),
    });
  }

  private clearTimers(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.maxTimer) clearTimeout(this.maxTimer);
    this.idleTimer = null;
    this.maxTimer = null;
  }
}
